import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2, X } from "lucide-react";
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { forwardRef } from "react";
import { cn } from "./cn.js";

const button = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-sm)] text-sm font-medium transition-all duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-ring)] disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary: "bg-[var(--accent)] text-[var(--accent-fg)] shadow-sm hover:brightness-110",
        secondary: "bg-[var(--surface-3)] text-[var(--fg)] hover:bg-[var(--line)]",
        outline:
          "border border-[var(--line-strong)] bg-transparent text-[var(--fg)] hover:bg-[var(--surface-2)]",
        ghost: "text-[var(--fg-muted)] hover:bg-[var(--surface-3)] hover:text-[var(--fg)]",
        danger: "bg-[var(--danger)] text-white hover:brightness-110",
      },
      size: { sm: "h-8 px-3", md: "h-10 px-4", lg: "h-11 px-6 text-base", icon: "size-9" },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(button({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

const field =
  "w-full rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--surface-2)] px-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-faint)] outline-none transition focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-ring)] disabled:opacity-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...p }, ref) {
    return <input ref={ref} className={cn(field, "h-10", className)} {...p} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...p }, ref) {
  return <textarea ref={ref} className={cn(field, "py-2.5 leading-relaxed", className)} {...p} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...p }, ref) {
    return <select ref={ref} className={cn(field, "h-10 pe-8", className)} {...p} />;
  },
);

export function Label({
  children,
  hint,
  htmlFor,
}: {
  children: ReactNode;
  hint?: string;
  htmlFor?: string;
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-medium text-[var(--fg-muted)]">
      {children}
      {hint && <span className="ms-2 font-normal text-[var(--fg-faint)]">{hint}</span>}
    </label>
  );
}

export function Card({ className, ...p }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius)] border border-[var(--line)] bg-[var(--surface)] p-5",
        className,
      )}
      {...p}
    />
  );
}

export function Badge({
  className,
  tone = "neutral",
  ...p
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: "neutral" | "accent" | "success" | "danger" | "warn";
}) {
  const tones = {
    neutral: "bg-[var(--surface-3)] text-[var(--fg-muted)]",
    accent: "bg-[var(--accent-soft)] text-[var(--accent-text)]",
    success: "bg-[color-mix(in_oklab,var(--success)_15%,transparent)] text-[var(--success)]",
    danger: "bg-[color-mix(in_oklab,var(--danger)_15%,transparent)] text-[var(--danger)]",
    warn: "bg-[color-mix(in_oklab,var(--warn)_18%,transparent)] text-[var(--warn)]",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
        tones[tone],
        className,
      )}
      {...p}
    />
  );
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <SwitchPrimitive.Root
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      className="relative h-6 w-11 shrink-0 rounded-full bg-[var(--line-strong)] outline-none transition disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-[var(--accent-ring)]"
    >
      <SwitchPrimitive.Thumb className="block size-5 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[22px] rtl:-translate-x-0.5 rtl:data-[state=checked]:-translate-x-[22px]" />
    </SwitchPrimitive.Root>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <Loader2
      className={cn("size-4 animate-spin text-[var(--fg-muted)]", className)}
      aria-label="Loading"
    />
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      {icon && (
        <div className="grid size-12 place-items-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent-text)]">
          {icon}
        </div>
      )}
      <h3 className="font-[var(--font-display)] text-lg font-semibold">{title}</h3>
      {hint && <p className="max-w-sm text-sm text-[var(--fg-muted)]">{hint}</p>}
      {action}
    </div>
  );
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm data-[state=open]:animate-[fade-in_.15s_ease-out]" />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex max-h-[92dvh] flex-col border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow)] outline-none",
            // Bottom sheet on phones, centered dialog on larger screens.
            "inset-x-0 bottom-0 rounded-t-3xl sm:inset-auto sm:start-1/2 sm:top-1/2 sm:w-[calc(100vw-2rem)] sm:-translate-y-1/2 sm:rounded-[var(--radius)] sm:ltr:-translate-x-1/2 sm:rtl:translate-x-1/2",
            wide ? "sm:max-w-2xl" : "sm:max-w-md",
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-[var(--line)] p-5">
            <div>
              <DialogPrimitive.Title className="font-[var(--font-display)] text-lg font-semibold">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-[var(--fg-muted)]">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">
                  {title}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              className="rounded-lg p-1.5 text-[var(--fg-muted)] hover:bg-[var(--surface-3)]"
              aria-label="Close"
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="overflow-y-auto p-5">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Official QN Ventures mark (same SVG as qnventures.uk): an N inside viewfinder corners. Fixed brand colours. */
export function Logo({ size = 28, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={className}
      role="img"
      aria-label="QN Ventures"
    >
      <rect width="32" height="32" rx="6" fill="#070b0a" />
      <g fill="none" strokeWidth="2" strokeLinecap="square">
        <path stroke="#eff6f2" d="M6 11V6h5M21 6h5v5M6 21v5h5" />
        <path stroke="#34d399" d="M26 21v5h-5" />
        <path stroke="#eff6f2" d="M11 21v-10l10 10V11" strokeLinejoin="miter" />
      </g>
    </svg>
  );
}

/** "AI" in the brand gradient, with an optional "by QN Ventures" tag. */
export function Wordmark({ size = "md", by = false }: { size?: "sm" | "md" | "lg"; by?: boolean }) {
  const cls = { sm: "text-base", md: "text-lg", lg: "text-3xl" }[size];
  return (
    <span
      className={cn(
        "inline-flex items-baseline gap-1.5 font-display font-bold tracking-tight",
        cls,
      )}
      dir="ltr"
    >
      <span className="brand-gradient">AI</span>
      {by && (
        <span className="text-[0.5em] font-medium tracking-wider text-[var(--fg-muted)]">
          by QN Ventures
        </span>
      )}
    </span>
  );
}
